import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Typography,
  CircularProgress,
  InputAdornment,
  DialogContentText,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";



// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const API_URL = API_ENDPOINTS.APP_USERS;

const AppUsersListContent = () => {
  const [appUsers, setAppUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortColumn, setSortColumn] = useState("");
  const [regionMap, setRegionMap] = useState({});
  const [selectedRegion, setSelectedRegion] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchAppUsers();
    fetchRegions();
  }, []);

  const fetchAppUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL.BASE);
      const data = response.data;
      let appUsersArray = [];
      if (Array.isArray(data)) {
        appUsersArray = data;
      } else if (data && Array.isArray(data.appUsers)) {
        appUsersArray = data.appUsers;
      }
      setAppUsers(appUsersArray);
    } catch (error) {
      console.error("Error fetching app users:", error);
      setAppUsers([]);
      setSnackbar({
        open: true,
        message: "Failed to load app users.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.REGION_NAMES);
      const map = {};
      (Array.isArray(res.data) ? res.data : []).forEach((r) => {
        map[r.id] = r.name;
      });
      setRegionMap(map);
    } catch (err) {
      // Optionally handle error
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = deleteDialog.id;
    setDeleteDialog({ open: false, id: null });
    try {
      await axios.delete(API_URL.DELETE(id));
      setAppUsers((prevAppUsers) => prevAppUsers.filter((appUser) => appUser.id !== id));
      setSnackbar({
        open: true,
        message: "App User deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting app user:", error);
      let userMessage = "Failed to delete app user.";
      if (error.response?.data?.message && error.response.data.message.includes("foreign key constraint fails")) {
        userMessage = "Cannot delete this user because they have associated customer orders. Please delete or reassign those orders first.";
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false, message: "", severity: "success" }));
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedRegion([]);
  };

  const openImportDialog = () => setImportDialogOpen(true);
  const closeImportDialog = () => {
    setImportDialogOpen(false);
    setSelectedFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      // Optional: formData.append('entity', 'app-users');

      const res = await axios.post(API_ENDPOINTS.IMPORT_CSV, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { message, errors, status } = res.data || {};
      let severity = "success";
      if (status === "partial_success") {
        severity = "warning";
      } else if (status === "error" || (Array.isArray(errors) && errors.length > 0)) {
        severity = "error";
      }

      let displayMessage = message || "CSV import completed.";
      if (Array.isArray(errors) && errors.length > 0) {
        const errorsText = errors.join(" | ");
        displayMessage = `${displayMessage} Errors: ${errorsText}`;
      }

      setSnackbar({ open: true, message: displayMessage, severity });
      closeImportDialog();
      fetchAppUsers();
    } catch (err) {
      const message = err.response?.data?.message || "CSV import failed. Please check the file and try again.";
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const filteredAppUsers = Array.isArray(appUsers)
    ? appUsers.filter((appUser) => {
        // Region filter
        if (selectedRegion.length > 0 && !selectedRegion.includes('all')) {
          const userRegionIds = Array.isArray(appUser.region_id)
            ? appUser.region_id
            : String(appUser.region_id || '').split(',').map(id => id.trim()).filter(Boolean);
          if (!selectedRegion.some(regionId => userRegionIds.includes(regionId))) {
            return false;
          }
        }
        // Search filter (existing)
        return [
          appUser.company_name || "",
          appUser.contact_person || "",
          appUser.mobile || "",
        ].some((field) =>
          field.toLowerCase().includes(searchTerm.trim().toLowerCase())
        );
      })
    : [];

  // Sorting logic for company name or created_at (only when a sort column is selected)
  const sortedAppUsers = sortColumn
    ? [...filteredAppUsers].sort((a, b) => {
        if (sortColumn === "company_name") {
          const nameA = (a.company_name || "").toLowerCase();
          const nameB = (b.company_name || "").toLowerCase();
          if (sortOrder === "asc") {
            return nameA.localeCompare(nameB);
          } else {
            return nameB.localeCompare(nameA);
          }
        } else if (sortColumn === "created_at") {
          const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
          const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
          if (sortOrder === "asc") {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        }
        return 0;
      })
    : filteredAppUsers;

  console.log("searchTerm", searchTerm, sortedAppUsers);

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", p: "25px" }}>
      <Box>
        <Box className="search-block"  sx={{ display: "flex", justifyContent: "space-between", mb: "25px" }}>
          <div className="search-left">
            <label>Search :</label>
            <input
              type="text"
              placeholder="Search by company or mobile..."  
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: "8px", width: "200px", borderRadius: "5px", border: "1px solid #ccc" }}
            />
            <FormControl sx={{ minWidth: 200, mr: 2, ml: 2 }} size="small">
              <InputLabel>Region</InputLabel>
              <Select
                multiple
                value={selectedRegion}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.includes('all')) {
                    setSelectedRegion(['all']);
                  } else {
                    setSelectedRegion(value);
                  }
                }}
                label="Region"
                renderValue={(selected) => {
                  if (selected.includes('all')) return 'All Regions';
                  return selected.length === 0
                    ? 'All'
                    : selected.map(id => regionMap[id] ?? 'Unknown').join(', ');
                }}
              >
                <MenuItem value="all">
                  <em>All Regions</em>
                </MenuItem>
                {Object.entries(regionMap).map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    <Checkbox checked={selectedRegion.includes(id)} />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          {user.permissions.includes("add-appUsers") ? (
            <div className="position-button">
              {/* Add User Button */}
              <Link to="/ecommerce/create-appUsers">
                <Button variant="contained" color="secondary">
                  <AddIcon /> Add New User
                </Button>
              </Link>

              {/* Download sample CSV */}
              <Button
                component="a"
                href="/sample-app-users.csv"
                download
                variant="outlined"
                color="secondary"
                sx={{ ml: 2 }}
              >
                Download sample CSV
              </Button>

              {/* Open Import Dialog */}
              <Button
                variant="contained"
                color="success"
                startIcon={<UploadFileIcon />}
                sx={{ ml: 2 }}
                onClick={openImportDialog}
              >
                Import CSV
              </Button>
            </div>
          ) : null}



        </Box>

        {user.permissions.includes("view-appUsers") ? (
          loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 2, py: 4 }}>
              <CircularProgress />
              <Typography>Loading users...</Typography>
            </Box>
          ) : (
            <>
              <TableContainer className="custom-table" component={Paper}>
                <Table sx={{ minWidth: 1000 }} aria-label="App Users Table">
                  <TableHead>
                    <TableRow>
                      <TableCell>S.No</TableCell>
                      <TableCell>Profile Pic</TableCell>
                      <TableCell>User Type</TableCell>
                      <TableCell
                        onClick={() => {
                          if (sortColumn === "company_name") {
                            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                          } else {
                            setSortColumn("company_name");
                            setSortOrder("asc");
                          }
                        }}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        User/Company Name
                        {sortColumn === "company_name" ? (
                          sortOrder === "asc" ? (
                            <ArrowUpwardIcon fontSize="small" sx={{ verticalAlign: "middle", ml: 0.5, color: 'primary.main' }} />
                          ) : (
                            <ArrowDownwardIcon fontSize="small" sx={{ verticalAlign: "middle", ml: 0.5, color: 'primary.main' }} />
                          )
                        ) : (
                          <ArrowUpwardIcon fontSize="small" sx={{ verticalAlign: "middle", ml: 0.5, color: 'action.disabled' }} />
                        )}
                      </TableCell>
                      <TableCell>Mobile</TableCell>
                      <TableCell
                        onClick={() => {
                          if (sortColumn === "created_at") {
                            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                          } else {
                            setSortColumn("created_at");
                            setSortOrder("asc");
                          }
                        }}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Registered On
                        {sortColumn === "created_at" ? (
                          sortOrder === "asc" ? (
                            <ArrowUpwardIcon fontSize="small" sx={{ verticalAlign: "middle", ml: 0.5, color: 'primary.main' }} />
                          ) : (
                            <ArrowDownwardIcon fontSize="small" sx={{ verticalAlign: "middle", ml: 0.5, color: 'primary.main' }} />
                          )
                        ) : (
                          <ArrowUpwardIcon fontSize="small" sx={{ verticalAlign: "middle", ml: 0.5, color: 'action.disabled' }} />
                        )}
                      </TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAppUsers.length > 0 ? (
                      sortedAppUsers
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((appUser, index) => (
                          <TableRow key={appUser.id}>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>
                              {appUser.profile_pic ? (
                                <img
                                  src={appUser.profile_pic}
                                  alt={appUser.company_name || "Profile"}
                                  style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: "50px",
                                    height: "50px",
                                    bgcolor: "grey.300",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <Typography variant="caption">No Image</Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>{appUser.user_type?.name || "N/A"}</TableCell>
                            <TableCell>{appUser.company_name || "N/A"}</TableCell>
                            <TableCell>{appUser.mobile || "N/A"}</TableCell>
                            <TableCell>{appUser.created_at ? new Date(appUser.created_at).toISOString().slice(0, 10) : "N/A"}</TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                {user.permissions.includes("view-appUsers") && (
                        <Link to={`/ecommerce/appUsers-list/details/${appUser.id}`}>
                                    <IconButton color="info" aria-label="View details">
                                      <VisibilityIcon />
                                    </IconButton>
                                  </Link>
                                )}
                                {user.permissions.includes("edit-appUsers") && (
                        <Link to={`/ecommerce/edit-appUsers/${appUser.id}`}>
                                    <IconButton color="primary" aria-label="Edit user">
                                      <EditIcon />
                                    </IconButton>
                                  </Link>
                                )}
                                {user.permissions.includes("delete-appUsers") && (
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeleteClick(appUser.id)}
                                    aria-label="Delete user"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No app users found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                        count={sortedAppUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                          setRowsPerPage(parseInt(event.target.value, 10));
                          setPage(0);
                        }}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </>
          )
        ) : (
          <Typography variant="body1" color="error" textAlign="center" sx={{ py: 4 }}>
            You do not have permission to view app users. Please contact your administrator.
          </Typography>
        )}
      </Box>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onClose={closeImportDialog} fullWidth maxWidth="sm">
        <DialogTitle>Import App Users (CSV)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a .csv file following the sample format. Upload starts when you click Upload.
          </Typography>
          <input type="file" accept=".csv" onChange={handleFileChange} />
          {selectedFile ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Selected file: {selectedFile.name}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeImportDialog} disabled={uploading}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" color="secondary" disabled={!selectedFile || uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}

      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} color="primary" variant="text" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="text" sx={{ fontWeight: 600 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AppUsersListContent;